import Vue from 'vue'
import api from '@services/api'
import fullstory from '@utils/fullstory'
import {
  HumanFields,
  AssignmentTables,
  AssetTypes,
  AssetObjectNames,
  smsStatuses,
} from '@constants/knack'
import {
  SET_ASSIGNED_ASSETS_RESPONSE,
  SET_ALL_ASSETS_RESPONSE,
  SET_ALL_ASSETS,
  SET_ASSIGNED_ASSETS,
  SET_ALL_ASSETS_DONE_LOADING,
  SET_ALL_ASSETS_STILL_LOADING,
  SET_ASSIGNMENTS_DONE_LOADING,
  SET_ASSIGNMENTS_STILL_LOADING,
  SELECT_ASSET,
  DESELECT_ASSET,
  DESELECT_ALL_ASSETS,
  SELECT_ASSGNT,
  DESELECT_ASSGNT,
  DESELECT_ALL_ASSGNTS,
  ADD_ASSET_ASSIGNMENT,
  UPDATE_ASSET_ASSIGNMENT,
  REMOVE_ASSET_ASSIGNMENT,
  SET_IS_PROCESSING,
  SET_NOT_PROCESSING,
  SET_ASSET_HOVER,
  UNSET_ASSET_HOVER,
  SET_MULTI_ASSIGN_PROPS,
  RESET_MULTI_ASSIGN_PROPS,
  MARK_SHIFTS_PENDING_STATUS,
  CLEAR_SHIFTS_PENDING_STATUS,
} from '@constants/mutations'
import nanoid from 'nanoid'
import smsStatusOf from '@utils/smsStatusOf'
import _get from 'lodash/get'
import _groupBy from 'lodash/groupBy'

// Pending shifts restored from sessionStorage older than this are dropped on
// read, so a tab reopened much later does not poll for a stale send. While a
// tab is open the board's poll window decides when to give up instead.
export const PENDING_STATUS_TTL_MS = 60 * 1000

// Knack filter for one asset type's assignments on one report date.
function assignedAssetsQuery(assetType, date, options = {}) {
  let assignmentType = AssignmentTables[assetType]
  let assignmentDateField = HumanFields[assignmentType].REPORT_DATE
  return {
    ...options,
    filters: {
      match: 'and',
      rules: [{ field: assignmentDateField, operator: 'is', value: date }],
    },
  }
}

// The api layer resolves with a { statusCode: 500 } object once its 429
// retries run out, and only then; read the return value so that case rejects
// instead of leaving the caller waiting for a callback that never fires.
async function fetchAssignedAssetsRecords(assetType, date, options) {
  let response = await api.getAssignedAssets(assetType, {
    options: assignedAssetsQuery(assetType, date, options),
    date,
  })
  if (!response || !Array.isArray(response.records)) {
    throw new Error(
      `Assigned ${assetType} read failed: ${_get(
        response,
        'body',
        'no records'
      )}`
    )
  }
  return response
}

// Overlapping reads (poll, modal re-read) land in any order. Only the read
// issued last may write the board, even when a newer read failed: a stale
// board beats one silently rolled back to a previous date or a pre-send
// snapshot. Keyed by asset type because SET_ASSIGNED_ASSETS is.
let issuedReadTickets = {}

function takeReadTicket(assetType) {
  issuedReadTickets[assetType] = (issuedReadTickets[assetType] || 0) + 1
  return issuedReadTickets[assetType]
}

function isNewestRead(assetType, ticket) {
  return ticket === issuedReadTickets[assetType]
}

export const ALL_ASSETS_CACHE_MS = 10 * 1000

// Reads of one asset type can ask Knack for different sets: the quick look
// wants STATUS is Active, the board wants STATUS is not Inactive, and the
// daily report cards filter materials and pay/work items by job type. Only an
// identical query may be served from the last read, or a provider gets back
// the set a different provider asked for.
export function allAssetsCacheKey(options) {
  return JSON.stringify([
    options.assetType,
    options.rows_per_page || null,
    options.filters || null,
  ])
}

// Also never skip when the type has nothing loaded: a read that came back
// empty, or one that never ran, must not pin an empty list for ten seconds.
function hasFreshAssets(state, assetType, cacheKey, now = Date.now()) {
  let fetchedAt = state.lastAssetsFetchTimes[cacheKey]
  return (
    Boolean(fetchedAt) &&
    now - fetchedAt < ALL_ASSETS_CACHE_MS &&
    (state.allAssets[assetType] || []).length > 0
  )
}

function unexpiredPendingStatus(pendingStatusShifts, now = Date.now()) {
  let kept = {}
  Object.entries(pendingStatusShifts).forEach(([shiftId, sentAt]) => {
    if (now - sentAt < PENDING_STATUS_TTL_MS) {
      kept[shiftId] = sentAt
    }
  })
  return kept
}

export default {
  state: {
    data: {},
    assets: [],
    // multiple report records (plus total_records and other metadata)
    // response.records contains the records
    assignedAssetsResponses: {},
    allAssetsResponses: {},
    allAssets: {},
    // query signature -> ms timestamp of its last read, see fetchAllAssets.
    lastAssetsFetchTimes: {},
    assignedAssets: {},
    // shift Knack id -> ms timestamp of the send that MURKs or Twilio accepted.
    // Persisted to sessionStorage (see store/index.js) so a page refresh inside
    // the queue's write window cannot re-offer a shift that is already on its way.
    pendingStatusShifts: {},
    areAllAssetsLoading: true,
    areAssignmentsLoading: true,
    selectedAssets: [],
    selectedAssgnts: [],
    hoveringAssetId: null,
    multiAssign: {
      asset: {},
      assgnts: [],
      existingAssgnt: false,
      targetReport: {},
    },
  },
  mutations: {
    //
    // Assets and Assignment
    //

    [SET_ASSIGNED_ASSETS_RESPONSE](state, { assetType, response }) {
      state.assignedAssetsResponses[assetType] = response
    },
    [SET_ALL_ASSETS_RESPONSE](state, { assetType, response }) {
      state.allAssetsResponses[assetType] = response
    },
    [SET_ALL_ASSETS](state, { assetType, assets, cacheKey }) {
      state.allAssets[assetType] = assets
      state.lastAssetsFetchTimes = {
        ...state.lastAssetsFetchTimes,
        [cacheKey]: Date.now(),
      }
    },
    [SET_ASSIGNED_ASSETS](state, { assetType, assets }) {
      state.assignedAssets = { ...state.assignedAssets, [assetType]: assets }
    },

    [ADD_ASSET_ASSIGNMENT](state, { assetType, assgnt, reportId }) {
      let newAssgnt = { ...assgnt, DAILY_REPORT: [{ id: reportId }] }
      let refreshedAssetsArray = state.assignedAssets[assetType]
      refreshedAssetsArray.push(newAssgnt)
      state.assignedAssets = {
        ...state.assignedAssets,
        [assetType]: refreshedAssetsArray,
      }
    }, // ADD_ASSET_ASSIGNMENT

    [UPDATE_ASSET_ASSIGNMENT](state, { assetType, assgnt, reportId }) {
      let existingIndex = state.assignedAssets[assetType].indexOf(
        state.assignedAssets[assetType].find((a) => a.ID === assgnt.ID)
      )
      // A background refresh may have replaced the board since this row was
      // read; -1 would write a junk key instead of the row.
      if (existingIndex < 0) {
        return
      }
      assgnt = { ...assgnt, DAILY_REPORT: [{ id: reportId }] }
      Vue.set(state.assignedAssets[assetType], existingIndex, assgnt)
      let refreshedAssetsArray = state.assignedAssets[assetType]
      state.assignedAssets = {
        ...state.assignedAssets,
        [assetType]: refreshedAssetsArray,
      }
    }, // UPDATE_ASSET_ASSIGNMENT

    [REMOVE_ASSET_ASSIGNMENT](state, { assetType, assgntId }) {
      let existingAssignment = state.assignedAssets[assetType].find(
        (a) => a.ID === assgntId
      )
      // Not found (a status poll swapped the board out from under a ghost
      // row): splice(-1, 1) would delete the last real assignment instead.
      if (!existingAssignment) {
        return
      }
      state.assignedAssets[assetType].splice(
        state.assignedAssets[assetType].indexOf(existingAssignment),
        1
      )
    }, // REMOVE_ASSET_ASSIGNMENT

    [MARK_SHIFTS_PENDING_STATUS](state, { shiftIds, sentAt = Date.now() }) {
      let pending = { ...state.pendingStatusShifts }
      shiftIds.forEach((shiftId) => {
        pending[shiftId] = sentAt
      })
      state.pendingStatusShifts = pending
    }, // MARK_SHIFTS_PENDING_STATUS

    [CLEAR_SHIFTS_PENDING_STATUS](state, { shiftIds }) {
      let pending = { ...state.pendingStatusShifts }
      shiftIds.forEach((shiftId) => {
        delete pending[shiftId]
      })
      state.pendingStatusShifts = pending
    }, // CLEAR_SHIFTS_PENDING_STATUS

    //
    // Loading
    //

    [SET_ALL_ASSETS_DONE_LOADING](state) {
      state.areAllAssetsLoading = false
    },
    [SET_ALL_ASSETS_STILL_LOADING](state) {
      state.areAllAssetsLoading = true
    },
    [SET_ASSIGNMENTS_DONE_LOADING](state) {
      state.areAssignmentsLoading = false
    },
    [SET_ASSIGNMENTS_STILL_LOADING](state) {
      state.areAssignmentsLoading = true
    },

    //
    // Selection
    //

    [SELECT_ASSET](state, assetId) {
      state.selectedAssets.push(assetId)
    },
    [DESELECT_ASSET](state, assetId) {
      let index = state.selectedAssets.indexOf(assetId)
      if (index !== -1) {
        state.selectedAssets.splice(index, 1)
      }
    },

    [DESELECT_ALL_ASSETS](state) {
      state.selectedAssets = []
    }, // DESELECT_ALL_ASSETS

    [SELECT_ASSGNT](state, assgntId) {
      state.selectedAssgnts.push(assgntId)
    },
    [DESELECT_ASSGNT](state, assgntId) {
      let index = state.selectedAssgnts.indexOf(assgntId)
      if (index !== -1) {
        state.selectedAssgnts.splice(index, 1)
      }
    },

    [DESELECT_ALL_ASSGNTS](state) {
      state.selectedAssgnts = []
    }, // DESELECT_ALL_ASSGNTS

    //
    // Processing
    //

    [SET_IS_PROCESSING](state, { objArrayName, assetType, assgntIndex }) {
      let assgntsArrayClone = [...state[objArrayName][assetType]]
      assgntsArrayClone[assgntIndex].isProcessing = true
      state[objArrayName] = {
        ...state[objArrayName],
        [assetType]: assgntsArrayClone,
      }
    }, // SET_IS_PROCESSING

    [SET_NOT_PROCESSING](state, { objArrayName, assetType, assgntIndex }) {
      let assgntsArrayClone = [...state[objArrayName][assetType]]
      assgntsArrayClone[assgntIndex].isProcessing = false
      state[objArrayName] = {
        ...state[objArrayName],
        [assetType]: assgntsArrayClone,
      }
    }, // SET_NOT_PROCESSING

    //
    // Hover
    //

    [SET_ASSET_HOVER](state, assetId) {
      state.hoveringAssetId = assetId
    },
    [UNSET_ASSET_HOVER](state) {
      state.hoveringAssetId = null
    },

    //
    // Multi Assgnt
    //

    [SET_MULTI_ASSIGN_PROPS](state, newProps) {
      state.multiAssign = newProps
    }, // SET_MULTI_ASSIGN_PROPS

    [RESET_MULTI_ASSIGN_PROPS](state) {
      state.multiAssign = {
        asset: {},
        assgnts: [],
        existingAssgnt: false,
        targetReport: {},
      }
    }, // RESET_MULTI_ASSIGN_PROPS
  }, // mutations
  actions: {
    // Fetch all assets of a type. Providers on the same screen repeat the same
    // query back to back, so a repeat inside the cache window is served from
    // the store. The loading flag clears in a finally: the skip used to leave
    // it on, and getAsset answers {} while it is on, which is how the publish
    // modal lost every laborer's phone number (#414).
    async fetchAllAssets({ getters, commit, state }, options) {
      let { assetType } = options
      let cacheKey = allAssetsCacheKey(options)
      commit(SET_ALL_ASSETS_STILL_LOADING)
      try {
        if (hasFreshAssets(state, assetType, cacheKey)) {
          return getters.getAllAssets(assetType)
        }
        return await api.getCollection(assetType, { options }, (response) => {
          commit(SET_ALL_ASSETS_RESPONSE, { response, assetType })
          commit(SET_ALL_ASSETS, {
            assets: response.records,
            assetType,
            cacheKey,
          })
        })
      } catch (err) {
        throw new Error(err)
      } finally {
        commit(SET_ALL_ASSETS_DONE_LOADING)
      }
    },

    // fetch Assignments by Date
    async fetchAssignedAssets({ commit, dispatch }, options) {
      try {
        commit(SET_ASSIGNMENTS_STILL_LOADING)
        await dispatch('refreshAssignedAssets', options)
      } catch (err) {
        throw new Error(err)
      } finally {
        commit(SET_ASSIGNMENTS_DONE_LOADING)
      }
    }, // fetchAssignedAssets

    // Same read as fetchAssignedAssets, but the board keeps rendering while it
    // runs: the loading flag is never touched. Used to pick up SMS statuses
    // written by MURKs and Twilio after a send.
    // An overtaken read is dropped and resolves with the board as it stands.
    async refreshAssignedAssets({ state, commit, dispatch }, options) {
      let { assetType, date, ...rest } = options
      let ticket = takeReadTicket(assetType)
      let response = await fetchAssignedAssetsRecords(assetType, date, rest)
      if (!isNewestRead(assetType, ticket)) {
        return state.assignedAssets[assetType] || []
      }
      commit(SET_ASSIGNED_ASSETS, { assets: response.records, assetType })
      commit(SET_ASSIGNED_ASSETS_RESPONSE, { response, assetType })
      dispatch('reconcilePendingStatusShifts', response.records)
      return response.records
    }, // refreshAssignedAssets

    markShiftsPendingStatus({ commit }, shiftIds) {
      commit(MARK_SHIFTS_PENDING_STATUS, { shiftIds })
    }, // markShiftsPendingStatus

    // The board stopped waiting: whatever Knack never reported on goes back to
    // being sendable. Called by the status poll when its window closes.
    clearPendingStatusShifts({ state, commit }) {
      let shiftIds = Object.keys(state.pendingStatusShifts)
      if (shiftIds.length) {
        commit(CLEAR_SHIFTS_PENDING_STATUS, { shiftIds })
      }
    }, // clearPendingStatusShifts

    // Forget a pending shift once Knack reports a status for it, or once it
    // has waited longer than PENDING_STATUS_TTL_MS.
    reconcilePendingStatusShifts({ state, commit }, assignments) {
      let pending = state.pendingStatusShifts
      let unexpired = unexpiredPendingStatus(pending)
      let reported = assignments
        .filter(
          (assgnt) => assgnt.ID in unexpired && smsStatusOf(assgnt).length
        )
        .map((assgnt) => assgnt.ID)
      let shiftIds = Object.keys(pending).filter(
        (shiftId) => !(shiftId in unexpired) || reported.includes(shiftId)
      )
      if (shiftIds.length) {
        commit(CLEAR_SHIFTS_PENDING_STATUS, { shiftIds })
      }
    }, // reconcilePendingStatusShifts

    async fetchAssignment(
      { commit, getters, dispatch },
      { assetType, assignmentId }
    ) {
      try {
        let assgnt = await api.getAssetAssignment(assetType, assignmentId)
        return assgnt
      } catch (error) {
        throw new Error(error)
      }
    }, // fetchAssignment

    async setAssgntCounts(
      { getters, dispatch },
      { numAssgnts, assetType, targetKey }
    ) {
      Object.keys(numAssgnts).map(async (sourceOrDestination) => {
        let sORd = sourceOrDestination
        numAssgnts[sORd][targetKey] = await getters.getReportAssignedAssets({
          assetType,
          reportId: numAssgnts[sORd].id,
        }).length
      })

      // If this is the 'updated' call, we want to check the counts
      // and update the reports accordingly
      if ((await targetKey) === 'updated') {
        await dispatch('checkAssgntCounts', { numAssgnts, assetType })
      }

      return numAssgnts
    }, // setAssgntCounts

    async checkAssgntCounts({ dispatch }, { numAssgnts, assetType }) {
      Object.keys(numAssgnts).map(async (sourceOrDestination) => {
        let sORd = sourceOrDestination
        if (
          numAssgnts[sORd].updated !== numAssgnts[sORd].initial &&
          [numAssgnts[sORd].updated, numAssgnts[sORd].initial].includes(0)
        ) {
          let hasAssgntsFieldKey = `HAS_${AssignmentTables[assetType]}_RECORDS`
          // eslint-disable-next-line
          console.log(
            `Setting ${sORd} Report ${hasAssgntsFieldKey} to ${!!numAssgnts[
              sORd
            ].updated}!`
          )
          try {
            let updatedReport = await dispatch('updateReport', {
              ID: numAssgnts[sORd].id,
              [hasAssgntsFieldKey]: !!numAssgnts[sORd].updated,
            })
            return Promise.resolve(updatedReport)
          } catch (error) {
            return Promise.reject(error)
          }
        }
      })
    }, // checkAssgntCounts

    async tryToReassignAsset(
      { commit, getters, dispatch },
      { assetType, assgnt, reportId, locationId }
    ) {
      let sourceReportId = assgnt.DAILY_REPORT[0].id

      // let numAssgnts = {
      //   source: { id: sourceReportId, initial: null, updated: null },
      //   destination: { id: reportId, initial: null, updated: null },
      // }
      // numAssgnts = await dispatch('setAssgntCounts', {
      //   numAssgnts,
      //   assetType,
      //   targetKey: 'initial',
      // })

      if (sourceReportId === reportId) {
        let assetName = getters.getAssetNameFromAssgnt(assetType, assgnt)
        Vue.prototype.$notify({
          type: 'warn',
          title: `${assetName} already assigned to target report`,
        })
        return false
      }
      dispatch('setAssgntProcessing', { assetType, assgntId: assgnt.ID })
      let assetId = getters.getAssetIdFromAssgnt(assetType, assgnt)
      let ghostAssgnt = await dispatch('createGhostAssgnt', {
        assetType,
        assetId,
        reportId,
      })
      try {
        let updatedAssignment = await api.updateAssignment(
          assetType,
          assgnt.ID,
          locationId
            ? { DAILY_REPORT: reportId, LOCATION: locationId }
            : { DAILY_REPORT: reportId }
        )

        await dispatch(
          'updateDefaultShiftItem',
          { assetType, shiftId: assgnt.ID, reportId },
          { root: true }
        )

        // Do this before updating the assignment
        // so the duplicate key warning wont hit
        dispatch('removeGhostAssgnt', {
          assetType,
          assgntId: ghostAssgnt.ID,
        })
        commit(UPDATE_ASSET_ASSIGNMENT, {
          assetType,
          assgnt: updatedAssignment,
          reportId: updatedAssignment.DAILY_REPORT[0].id,
        })
        commit(DESELECT_ASSGNT, updatedAssignment.ID)
        let assetName = getters.getAssetNameFromAssgnt(
          assetType,
          updatedAssignment
        )
        await dispatch('makeRevisionForAssetMod', {
          assetType,
          assetName,
          action: 'added',
          reportId: updatedAssignment.DAILY_REPORT[0].id,
        })
        await dispatch('makeRevisionForAssetMod', {
          assetType,
          assetName,
          action: 'deleted',
          reportId: sourceReportId,
        })
      } catch (error) {
        dispatch('removeGhostAssgnt', {
          assetType,
          assgntId: ghostAssgnt.ID,
        })
        dispatch('setAssgntNotProcessing', {
          assetType,
          assgntId: assgnt.ID,
        })
        return Promise.reject(error)
      }
    }, // tryToReassignAsset

    async tryToAssignAsset(
      { commit, getters, dispatch },
      { assetType, assetId, reportId, locationId }
    ) {
      // let numAssgnts = {
      //   destination: { id: reportId, initial: null, updated: null },
      // }
      // numAssgnts = await dispatch('setAssgntCounts', {
      //   numAssgnts,
      //   assetType,
      //   targetKey: 'initial',
      // })

      let assignmentKey = getters.getAssetAssignmentKey(assetType)

      let ghostAssgnt = await dispatch('createGhostAssgnt', {
        assetType,
        assetId,
        reportId,
      })

      // The key on the Asset that will give you its name
      let assetNameKey = `${assignmentKey}_NAME`

      // The key on the Assignment that will store the name
      let assignmentNameFieldKey = `${AssignmentTables[assetType]}_NAME`

      let assetIdentifier = _get(ghostAssgnt, assetNameKey, 'BLANK RECORD')

      try {
        let assgnt = {
          [assignmentNameFieldKey]: assetIdentifier,
          DAILY_REPORT: reportId,
          [assignmentKey]: assetId,
        }
        if (locationId) {
          assgnt.LOCATION = locationId
        }
        if (assetType === AssetTypes.EQUIPMENT) {
          let asset = await getters.getAsset(assetType, assetId)
          if (asset.DEFAULT_NOTES.length) {
            assgnt.EQUIPMENT_NOTES = asset.DEFAULT_NOTES
          }
        } else if (assetType === AssetTypes.LABORER) {
          let asset = await getters.getAsset(assetType, assetId)
          let laborRateKnackId = _get(asset, 'LABOR_RATE[0].id')
          if (laborRateKnackId) {
            assgnt.LABOR_RATE = laborRateKnackId
          }
        }

        let newAssignment = await api.createAssignment(assetType, assgnt)

        let newAssignmentEventPayload = {
          assetType,
          [assignmentNameFieldKey]: assetIdentifier,
          assetId,
          reportId,
        }
        let newAssignmentStatus = newAssignment ? 'ok' : 'fail'

        fullstory.event(
          `create_assgnt.${newAssignmentStatus}`,
          newAssignmentEventPayload
        )

        let defaultShiftItemEventPayload = {
          assetType,
          shiftId: newAssignment.ID,
          reportId,
        }
        let defaultShiftItemRes = await dispatch(
          'addDefaultShiftItem',
          { assetType, shiftId: newAssignment.ID, reportId },
          { root: true }
        )

        let defaultShiftItemStatus = defaultShiftItemRes ? 'ok' : 'fail'
        fullstory.event(
          `create_default_shift_item.${defaultShiftItemStatus}`,
          defaultShiftItemEventPayload
        )

        // Do this before updating the assignment
        // so the duplicate key warning wont hit
        await dispatch('removeGhostAssgnt', {
          assetType,
          assgntId: ghostAssgnt.ID,
        })
        commit(ADD_ASSET_ASSIGNMENT, {
          assetType,
          assgnt: newAssignment,
          reportId: newAssignment.DAILY_REPORT[0].id,
        })
        commit(DESELECT_ASSET, assetId)
        let assetName = getters.getAssetNameFromAssgnt(assetType, newAssignment)
        await dispatch('makeRevisionForAssetMod', {
          assetType,
          assetName,
          action: 'added',
          reportId: newAssignment.DAILY_REPORT[0].id,
        })
      } catch (error) {
        dispatch('removeGhostAssgnt', {
          assetType,
          assgntId: ghostAssgnt.ID,
        })
        return Promise.reject(error)
      }
    }, // tryToAssignAsset

    async tryToUnassignAsset(
      { commit, dispatch, getters },
      { assetType, assgntId }
    ) {
      let assgnt = await getters.getAssgnt(assetType, assgntId)
      let assetName = getters.getAssetNameFromAssgnt(assetType, assgnt)
      // let numAssgnts = {
      //   source: { id: assgnt.DAILY_REPORT[0].id, initial: null, updated: null },
      // }
      // numAssgnts = await dispatch('setAssgntCounts', {
      //   numAssgnts,
      //   assetType,
      //   targetKey: 'initial',
      // })

      dispatch('setAssgntProcessing', { assetType, assgntId })
      try {
        let deleteRes = await api.deleteAssignment(assetType, assgntId)
        if (deleteRes.delete === true) {
          commit(REMOVE_ASSET_ASSIGNMENT, { assetType, assgntId })
          commit(DESELECT_ASSGNT, assgntId)

          await dispatch('makeRevisionForAssetMod', {
            assetType,
            assetName,
            action: 'deleted',
            reportId: assgnt.DAILY_REPORT[0].id,
          })

          return true
        } else {
          throw Error({
            deleteRes,
            msg: 'Asset could not be unassigned',
          })
        }
      } catch (error) {
        dispatch('setAssgntNotProcessing', { assetType, assgntId })
        return Promise.reject(error)
      }
    }, // tryToUnassignAsset

    createGhostAssgnt({ commit, getters }, { assetType, assetId, reportId }) {
      let ghostAssgnt = getters.makeGhostAssgnt(assetType, assetId, reportId)
      commit(ADD_ASSET_ASSIGNMENT, {
        assetType,
        assgnt: ghostAssgnt,
        reportId,
      })
      return ghostAssgnt
    }, // createGhostAssgnt

    removeGhostAssgnt({ commit }, { assetType, assgntId }) {
      commit(REMOVE_ASSET_ASSIGNMENT, {
        assetType,
        assgntId,
      })
    }, // createGhostAssgnt

    selectAssgnt({ commit }, assgntId) {
      commit(SELECT_ASSGNT, assgntId)
    }, // selectAssgnt

    deselectAssgnt({ commit }, assgntId) {
      commit(DESELECT_ASSGNT, assgntId)
    }, // deselectAssgnt

    deselectAllAssgnts({ commit }) {
      commit(DESELECT_ALL_ASSGNTS)
    }, // deselectAllAssgnts

    selectAsset({ commit }, assetId) {
      commit(SELECT_ASSET, assetId)
    }, // selectAsset

    deselectAsset({ commit }, assetId) {
      commit(DESELECT_ASSET, assetId)
    }, // deselectAsset

    deselectAllAssets({ commit }) {
      commit(DESELECT_ALL_ASSETS)
    }, // deselectAllAssets

    deselectAllAssgntsAndAssets({ commit }) {
      commit(DESELECT_ALL_ASSGNTS)
      commit(DESELECT_ALL_ASSETS)
    }, // deselectAllAssgntsAndAssets

    setAssetHover({ commit }, assetId) {
      commit(SET_ASSET_HOVER, assetId)
    }, // setAssetHover

    unsetAssetHover({ commit }) {
      commit(UNSET_ASSET_HOVER)
    }, // unsetAssetHover

    setAssgntProcessing({ commit, getters }, { assetType, assgntId }) {
      let assgntIndex = getters.getIndexOfAssignment(assetType, assgntId)
      commit(SET_IS_PROCESSING, {
        objArrayName: 'assignedAssets',
        assetType,
        assgntIndex,
      })
    }, // setAssgntProcessing

    setAssgntNotProcessing({ commit, getters }, { assetType, assgntId }) {
      let assgntIndex = getters.getIndexOfAssignment(assetType, assgntId)
      commit(SET_NOT_PROCESSING, {
        objArrayName: 'assignedAssets',
        assetType,
        assgntIndex,
      })
    }, // setAssgntNotProcessing

    setMultiAssgnProps(
      { commit, getters },
      { assetId, assetType, assgnts, existingAssgnt, targetReport }
    ) {
      let asset = getters.getAsset(assetType, assetId)
      commit(SET_MULTI_ASSIGN_PROPS, {
        asset,
        assgnts,
        existingAssgnt,
        targetReport,
      })
    }, // setMultiAssgnProps

    resetMultiAssgnProps({ commit }) {
      commit(RESET_MULTI_ASSIGN_PROPS)
    }, // resetMultiAssgnProps

    async clearLaborerSMSStatus({ commit }, { shiftId }) {
      try {
        let updatedAssignment = await api.updateAssignment(
          AssetTypes.LABORER,
          shiftId,
          { ASSIGNMENT_SMS_STATUS: null }
        )
        commit(UPDATE_ASSET_ASSIGNMENT, {
          assetType: AssetTypes.LABORER,
          assgnt: updatedAssignment,
          reportId: updatedAssignment.DAILY_REPORT[0].id,
        })
      } catch (error) {
        console.log(error)
        throw error
      }
    }, // clearLaborerSMSStatus

    async markLaborerSMSSkipped({ commit }, { shiftId }) {
      try {
        let updatedAssignment = await api.updateAssignment(
          AssetTypes.LABORER,
          shiftId,
          { ASSIGNMENT_SMS_STATUS: smsStatuses.SKIPPED_MANUALLY }
        )
        commit(UPDATE_ASSET_ASSIGNMENT, {
          assetType: AssetTypes.LABORER,
          assgnt: updatedAssignment,
          reportId: updatedAssignment.DAILY_REPORT[0].id,
        })
      } catch (error) {
        console.log(error)
        throw error
      }
    }, // markLaborerSMSSkipped
  }, // actions

  getters: {
    getAsset: (state, getters) => (assetType, assetId) => {
      if (state.areAllAssetsLoading) {
        return {}
      }
      return (state.allAssets[assetType] || []).find(
        (asset) => asset.ID === assetId
      )
    },

    getAssgnt: (state, getters) => (assetType, assgntId) => {
      if (state.areAssignmentsLoading) {
        return {}
      }
      return getters
        .getAssignedAssets(assetType)
        .find((assgnt) => assgnt.ID === assgntId)
    },

    getAllAssetsResponses: (state) => (assetType) =>
      state.allAssetsResponses[assetType] || [],

    getAssignedAssetsResponses: (state) => (assetType) =>
      state.assignedAssetsResponses[assetType] || [],

    getDispatcherReportAssets: (state, getters) => ({
      assetType,
      reportId,
    }) => {
      let assets = getters.getAssignedAssets(assetType) || []
      if (assets.length) {
        return assets.filter(
          (a) => a.DAILY_REPORT[0] && a.DAILY_REPORT[0].id === reportId
        )
      }
      return assets
    },

    getAssignedAssets: (state) => (assetType) =>
      state.assignedAssets[assetType] || [],

    // Shift ids accepted for sending whose Knack status has not come back yet.
    getPendingStatusShiftIds: (state) =>
      Object.keys(unexpiredPendingStatus(state.pendingStatusShifts)),

    getReportAssignedAssets: (state) => ({ reportId, assetType }) => {
      let reportAssets = state.assignedAssets[assetType].filter(
        (a) => a.DAILY_REPORT[0].id === reportId
      )
      return reportAssets || []
    },

    getIsAssetAssigned: (state, getters) => (assetType, assetId) => {
      if (state.areAssignmentsLoading) {
        return false
      }
      return getters.getAssetAssignments(assetType, assetId).length
    },

    getSelectedAssgnts: (state) => {
      return state.selectedAssgnts
    },

    getAssetAssignments: (state, getters) => (assetType, assetId) => {
      if (state.areAssignmentsLoading) {
        return false
      }
      let assignedAssets = getters.getAssignedAssets(assetType)
      let assignmentKey = getters.getAssetAssignmentKey(assetType)
      let assignments = assignedAssets.filter((a) =>
        a[assignmentKey].length
          ? a[assignmentKey][0].id === assetId && !a.isGhost
          : false
      )
      return assignments
    },

    getAssignmentsGroupedByAsset: (state, getters) => (
      assetType,
      assetsToGroup = null
    ) => {
      if (state.areAssignmentsLoading) {
        return false
      }
      assetsToGroup = assetsToGroup || getters.getAssignedAssets(assetType)
      let assignmentKey = getters.getAssetAssignmentKey(assetType)
      return _groupBy(assetsToGroup, (asset) =>
        _get(asset, `${assignmentKey}[0].id`)
      )
    }, // getAssignmentsGroupedByAsset

    numSelectedAssetsAndAssgnts: (state, getters) => {
      return (
        getters.getSelectedAssets.length + getters.getSelectedAssgnts.length
      )
    }, // numSelectedAssetsAndAssgnts

    getAreAssgntsOrAssetsSelected: (state, getters) => {
      return getters.numSelectedAssetsAndAssgnts > 0
    }, // getAreAssgntsOrAssetsSelected

    getAllAssets: (state) => (assetType) => state.allAssets[assetType] || [],

    getAvailableAssets: (state, getters) => (assetType) => {
      let assignmentKey = getters.getAssetAssignmentKey(assetType)
      let assignedAssets = getters.getAssignedAssets(assetType) || []
      let assignedIds = assignedAssets.map((a) =>
        a[assignmentKey][0] ? a[assignmentKey][0].id : false
      )
      let allAssets = getters.getAllAssets(assetType) || []
      let diff = allAssets.filter((a) => !assignedIds.includes(a.ID))
      return diff
    },

    getAssetsForDefaultForeman: (state, getters) => (assetType, foremanId) => {
      let allAssets = getters.getAllAssets(assetType) || []
      return allAssets.filter((asset) => {
        if (asset.DEFAULT_FOREMAN.length) {
          return asset.DEFAULT_FOREMAN[0].id === foremanId
        }
        return false
      })
    }, // getAssetsForDefaultForeman

    getSelectedAssets: (state) => {
      return state.selectedAssets
    },

    getIsAssetSelected: (state) => (assetId) => {
      return state.selectedAssets.includes(assetId)
    },

    getIsAssgntSelected: (state) => (assgntId) => {
      return state.selectedAssgnts.includes(assgntId)
    },

    getAssetAssignmentKey: (state) => (assetType) =>
      _get(AssetObjectNames, assetType),

    getAssetIdFromAssgnt: (state, getters) => (assetType, assgnt) => {
      let assgntKey = getters.getAssetAssignmentKey(assetType)
      return _get(assgnt, `[${assgntKey}][0].id`)
    }, // getAssetIdFromAssgnt

    getAssetNameFromAssgnt: (state, getters) => (assetType, assgnt) => {
      let assgntKey = getters.getAssetAssignmentKey(assetType)
      return _get(assgnt, `[${assgntKey}][0].identifier`, 'BLANK RECORD')
    }, // getAssetNameFromAssgnt

    getHoveringAssetId: (state) => state.hoveringAssetId,

    makeGhostAssgnt: (state, getters) => (assetType, assetId) => {
      let assignmentKey = getters.getAssetAssignmentKey(assetType)
      let asset = getters.getAsset(assetType, assetId)
      return {
        ...asset,
        ID: nanoid(),
        [assignmentKey]: [{ id: assetId }],
        isGhost: true,
      }
    }, // makeGhostAssgnt

    getIndexOfAssignment: (state) => (assetType, assgntId) => {
      let existingIndex = state.assignedAssets[assetType].indexOf(
        state.assignedAssets[assetType].find((a) => a.ID === assgntId)
      )
      return existingIndex
    },

    getMultiAssgnObj: (state) => state.multiAssign, // getMultiAssgnObj
  },
}
